<template>
  <section class="mt-4" aria-label="Autorizaciones por pasajero">
    <v-progress-linear v-if="cargando" indeterminate color="primary" class="mb-4" />
    <v-alert v-if="errorCarga" type="error" outlined>
      {{ errorCarga }} <v-btn small text @click="cargar()">Reintentar</v-btn>
    </v-alert>
    <template v-else-if="!cargando">
      <v-alert v-if="!filas.length" type="info" outlined>No hay pasajeros registrados para enviar autorizaciones individuales.</v-alert>
      <template v-else>
        <v-row align="center" dense>
          <v-col cols="12" sm="4">
            <v-checkbox :input-value="todosSeleccionados" :indeterminate="seleccionParcial" label="Seleccionar todos"
              :disabled="enviando || !pendientes.length" hide-details class="mt-0" @change="seleccionarTodos" />
          </v-col>
          <v-col cols="12" sm="4">
            <v-text-field v-model="importeRepartir" label="Importe a repartir (€)" inputmode="decimal" outlined dense hide-details :disabled="enviando" />
          </v-col>
          <v-col cols="12" sm="4">
            <v-chip class="px-10" label color="green lighten-2" text-color="white" link
              style="min-width: 250px;" :disabled="enviando || !seleccionados.length" @click="repartir">
              <span class="white--text">Repartir a partes iguales</span>
            </v-chip>
          </v-col>
        </v-row>
        <v-alert v-if="errorReparto" type="warning" dense outlined class="mt-3">{{ errorReparto }}</v-alert>
        <v-data-table :headers="headers" :items="filas" item-key="pasajero_id" :items-per-page="-1" hide-default-footer
          disable-sort class="mt-4" :mobile-breakpoint="700">
          <template v-slot:[`item.seleccionada`]="{ item }">
            <v-simple-checkbox v-model="item.seleccionada" :disabled="enviando || !puedeSeleccionar(item)" :aria-label="'Seleccionar a ' + item.nombre" @input="actualizarReparto" />
          </template>
          <template v-slot:[`item.action`]="{ item }">
            <v-chip class="mr-2 px-0" label small color="blue lighten-2" text-color="white" link
              :disabled="enviando" :aria-label="'Editar pasajero ' + item.nombre" @click="$emit('editar-pasajero', item.pasajero)">
              <v-icon>mdi-pencil</v-icon>
            </v-chip>
          </template>
          <template v-slot:[`item.nombre`]="{ item }">
            <div>{{ item.nombre }}</div>
            <div v-if="!tieneEmail(item)" class="error--text caption" role="alert">Este pasajero no tiene email.</div>
            <v-text-field v-else v-model="item.email_destinatario" :aria-label="'Correo de ' + item.nombre" type="email"
              placeholder="Sin correo" dense single-line hide-details="auto" class="correo-subtitulo mt-0 pt-0"
              :disabled="enviando || !item.seleccionada || completada(item)" maxlength="255" />
            <div v-if="item.error" class="error--text caption" role="alert">{{ item.error }}</div>
          </template>
          <template v-slot:[`item.roaming`]="{ item }">{{ roaming(item) }}</template>
          <template v-slot:[`item.autorizacion_importecarga`]="{ item }">
            <v-text-field v-model="item.autorizacion_importecarga" :aria-label="'Importe de ' + item.nombre" inputmode="decimal" dense outlined hide-details="auto"
              :disabled="enviando || !item.seleccionada || completada(item)" class="campo-importe" />
          </template>
          <template v-slot:[`item.autorizacion_importedeposito`]="{ item }">
            <v-text-field v-model="item.autorizacion_importedeposito" :aria-label="'Depósito de ' + item.nombre" inputmode="decimal" dense outlined hide-details="auto"
              :disabled="enviando || !item.seleccionada || completada(item)" class="campo-importe" />
          </template>
          <template v-slot:[`item.resto`]="{ item }">{{ resto(item) }}</template>
        </v-data-table>
        <v-row dense class="mt-4" aria-live="polite">
          <v-col cols="12" sm="4">
            <div>Total reserva</div>
            <div class="importe-resumen" :class="diferencia === 0 ? 'success--text' : 'error--text'">{{ euros(totalCentimos) }}</div>
          </v-col>
          <v-col cols="12" sm="4">
            <div>Total tras enviar seleccionados</div>
            <div class="importe-resumen" :class="diferencia === 0 ? 'success--text' : 'error--text'">{{ euros(totalAsignado) }}</div>
          </v-col>
          <v-col cols="12" sm="4">
            <div>Diferencia</div>
            <div class="importe-resumen" :class="diferencia === 0 ? 'success--text' : 'error--text'">{{ euros(diferencia) }}</div>
          </v-col>
        </v-row>
        <v-alert v-if="errorEnvio" type="error" outlined role="alert">{{ errorEnvio }}</v-alert>
        <v-alert v-if="mensaje" :type="hayFallos ? 'warning' : 'success'" outlined role="status">{{ mensaje }}</v-alert>
        <v-row v-if="mostrarBotonEnvio" justify="end" dense class="mt-4">
          <v-btn color="primary" :loading="enviando" :disabled="!puedeEnviar" @click="enviar()">
            Enviar a seleccionados ({{ seleccionados.length }})
          </v-btn>
        </v-row>
      </template>
    </template>
  </section>
</template>

<script>
import { actualizarPasajeroSolicitud } from '@/helpers/pasajerosSolicitud';
import { listarAutorizacionesTarjeta, enviarAutorizacionesTarjeta } from '@/services/autorizacionesTarjeta';
import { centimos, importeTexto, repartirCentimos, validarFilaAutorizacion } from '@/helpers/autorizacionesTarjeta';

export default {
  props: {
    reservaId: { type: [Number, String], required: true },
    pasajeros: { type: Array, default: () => [] },
    totalReserva: { type: [Number, String], default: 0 },
    accessToken: { type: String, required: true },
    mostrarBotonEnvio: { type: Boolean, default: true },
    obtenerHabitacion: { type: Function, default: () => null },
  },
  data: () => ({
    filas: [], registros: [], cargando: true, enviando: false,
    errorCarga: '', errorEnvio: '', errorReparto: '', mensaje: '', hayFallos: false,
    importeRepartir: '', solicitud: 0, requiereActualizar: false, envioRealizado: false, autorizacionesEnviadas: [],
    headers: [
      { text: 'Enviar', value: 'seleccionada', width: 65 },
      { text: '', value: 'action', width: 60, sortable: false },
      { text: 'Pasajero', value: 'nombre' }, { text: 'Rooming', value: 'roaming' },
      { text: 'Importe (€)', value: 'autorizacion_importecarga' },
      { text: 'Depósito (€)', value: 'autorizacion_importedeposito' },
      { text: 'Resto (€)', value: 'resto' },
    ],
  }),
  computed: {
    seleccionados() { return this.filas.filter(fila => fila.seleccionada && this.puedeSeleccionar(fila)); },
    pendientes() { return this.filas.filter(fila => this.puedeSeleccionar(fila)); },
    todosSeleccionados() { return this.pendientes.length > 0 && this.seleccionados.length === this.pendientes.length; },
    seleccionParcial() { return this.seleccionados.length > 0 && !this.todosSeleccionados; },
    seleccionadosInvalidos() { return this.seleccionados.filter(fila => validarFilaAutorizacion(fila)); },
    totalCentimos() { return centimos(this.totalReserva) || 0; },
    totalAsignado() {
      const ids = this.seleccionados.map(fila => Number(fila.pasajero_id));
      const guardado = this.registros.filter(registro => !ids.includes(Number(registro.pasajero_id)))
        .reduce((total, registro) => total + (centimos(registro.autorizacion_importecarga) || 0), 0);
      return guardado + this.seleccionados.reduce((total, fila) => total + (centimos(fila.autorizacion_importecarga) || 0), 0);
    },
    diferencia() { return this.totalCentimos - this.totalAsignado; },
    puedeEnviar() {
      return !this.cargando && !this.enviando && !this.requiereActualizar && !this.errorCarga && this.seleccionados.length > 0 && this.seleccionadosInvalidos.length === 0;
    },
  },
  watch: {
    pasajeros() { this.reconstruirFilas(true, true); },
    reservaId() {
      this.autorizacionesEnviadas = [];
      this.envioRealizado = false;
      this.cargar();
    },
    enviando(valor) { this.$emit('enviando', valor); },
  },
  mounted() { this.cargar(); },
  beforeDestroy() { this.solicitud++; },
  methods: {
    tieneEmail(fila) { return !!String(fila.pasajero.email || '').trim(); },
    puedeSeleccionar(fila) { return this.tieneEmail(fila) && !this.completada(fila); },
    validarFila: validarFilaAutorizacion,
    completada(fila) { return !!(fila.registro && (fila.registro.completado || fila.registro.completado_at)); },
    euros(valor) { return importeTexto(valor) + ' €'; },
    roaming(fila) {
      const habitacion = this.obtenerHabitacion(fila.pasajero);
      return habitacion ? habitacion.label : '—';
    },
    resto(fila) {
      const importe = centimos(fila.autorizacion_importecarga);
      const deposito = centimos(fila.autorizacion_importedeposito);
      return importe === null || deposito === null || deposito > importe ? '—' : importeTexto(importe - deposito);
    },
    reconstruirFilas(conservar = false, actualizarPasajeros = false) {
      const anteriores = this.filas;
      this.filas = this.pasajeros.map((pasajero, indice) => {
        const registro = this.registros.find(item => Number(item.pasajero_id) === Number(pasajero.id)) || null;
        const anterior = anteriores.find(item => Number(item.pasajero_id) === Number(pasajero.id));
        if (actualizarPasajeros && anterior) return actualizarPasajeroSolicitud(anterior, pasajero);
        const mantener = conservar && anterior && anterior.seleccionada && !(registro && registro.completado);
        return {
          pasajero_id: pasajero.id,
          pasajero,
          nombre: [pasajero.nombre, pasajero.primer_apellido, pasajero.segundo_apellido].filter(Boolean).join(' ') || 'Pasajero ' + pasajero.id,
          email_destinatario: mantener ? anterior.email_destinatario : registro ? registro.email_destinatario : pasajero.email || '',
          autorizacion_importecarga: mantener ? anterior.autorizacion_importecarga : registro ? registro.autorizacion_importecarga : '',
          autorizacion_importedeposito: mantener ? anterior.autorizacion_importedeposito : registro ? registro.autorizacion_importedeposito : indice === 0 ? '100.00' : '0.00',
          seleccionada: !!mantener && !!String(pasajero.email || '').trim(), registro, error: mantener ? anterior.error : '',
        };
      });
    },
    async cargar(conservar = false) {
      if (this.enviando) return;
      const solicitud = ++this.solicitud;
      this.cargando = true;
      this.errorCarga = '';
      try {
        const registros = await listarAutorizacionesTarjeta(this.accessToken, this.reservaId);
        if (solicitud !== this.solicitud) return;
        this.registros = registros;
        this.reconstruirFilas(conservar === true && !this.requiereActualizar);
        if (this.requiereActualizar) {
          this.mensaje = 'Comprueba el envío antes de volver a seleccionar destinatarios.';
          this.hayFallos = true;
          this.errorEnvio = '';
        }
        this.requiereActualizar = false;
        this.actualizarReparto();
      } catch (error) {
        if (solicitud === this.solicitud) this.errorCarga = 'No se han podido cargar las autorizaciones. Reintenta antes de enviar.';
      } finally {
        if (solicitud === this.solicitud) this.cargando = false;
      }
    },
    seleccionarTodos(valor) {
      this.filas.forEach(fila => { fila.seleccionada = !!valor && this.puedeSeleccionar(fila); });
      this.actualizarReparto();
    },
    actualizarReparto() {
      const ids = this.seleccionados.map(fila => Number(fila.pasajero_id));
      const otros = this.registros.filter(registro => !ids.includes(Number(registro.pasajero_id)))
        .reduce((total, registro) => total + (centimos(registro.autorizacion_importecarga) || 0), 0);
      this.importeRepartir = importeTexto(Math.max(0, this.totalCentimos - otros));
      this.errorReparto = '';
    },
    repartir() {
      const total = centimos(this.importeRepartir);
      const reparto = repartirCentimos(total, this.seleccionados.length);
      if (!reparto.length || reparto.some(importe => importe > 9999999999)) {
        this.errorReparto = 'Indica un importe válido que permita asignar al menos un céntimo a cada seleccionado.';
        return;
      }
      this.seleccionados.forEach((fila, indice) => {
        fila.autorizacion_importecarga = importeTexto(reparto[indice]);
        const deposito = centimos(fila.autorizacion_importedeposito);
        fila.autorizacion_importedeposito = importeTexto(Math.min(deposito === null ? (this.filas.indexOf(fila) === 0 ? 10000 : 0) : deposito, reparto[indice]));
      });
      this.errorReparto = '';
    },
    aplicarResultadoExterno(resultado) {
      (resultado.autorizaciones || []).forEach(registro => {
        const i = this.registros.findIndex(r => Number(r.id) === Number(registro.id));
        if (i < 0) this.registros.push(registro); else this.$set(this.registros, i, registro);
      });
      this.seleccionados.forEach(fila => {
        const envio = resultado.envios.find(e => Number(e.pasajero_id) === Number(fila.pasajero_id));
        if (envio && envio.enviado) {
          const r = (resultado.autorizaciones || []).find(r => Number(r.pasajero_id) === Number(fila.pasajero_id));
          if (r) this.autorizacionesEnviadas.push({ id: r.id, email_destinatario: r.email_destinatario });
          fila.seleccionada = false;
        } else fila.error = 'No se pudo enviar. Puedes reintentarlo.';
      });
      this.reconstruirFilas(true); this.actualizarReparto();
    },
    async enviar(correo = {}) {
      if (!this.puedeEnviar) return false;
      const seleccionados = this.seleccionados.slice();
      const solicitud = this.solicitud;
      this.enviando = true;
      this.errorEnvio = '';
      this.mensaje = '';
      seleccionados.forEach(fila => { fila.error = ''; });
      try {
        const resultado = await enviarAutorizacionesTarjeta(this.accessToken, this.reservaId, seleccionados.map(fila => ({
          pasajero_id: fila.pasajero_id,
          email_destinatario: fila.email_destinatario.trim(),
          autorizacion_importecarga: importeTexto(centimos(fila.autorizacion_importecarga)),
          autorizacion_importedeposito: importeTexto(centimos(fila.autorizacion_importedeposito)),
        })), correo);
        if (solicitud !== this.solicitud) return;
        if (!Array.isArray(resultado.envios) || !Array.isArray(resultado.data)) throw new Error('Respuesta de envío incompleta');
        resultado.data.forEach(registro => {
          const indice = this.registros.findIndex(item => item.id === registro.id);
          if (indice === -1) this.registros.push(registro);
          else this.$set(this.registros, indice, registro);
        });
        let enviados = 0;
        seleccionados.forEach(fila => {
          const envio = resultado.envios.find(item => Number(item.pasajero_id) === Number(fila.pasajero_id));
          if (envio && envio.enviado) {
            const registro = resultado.data.find(item => Number(item.pasajero_id) === Number(fila.pasajero_id));
            if (registro) {
              this.autorizacionesEnviadas = this.autorizacionesEnviadas.filter(item => item.id !== registro.id);
              this.autorizacionesEnviadas.push({ id: registro.id, email_destinatario: registro.email_destinatario });
            }
            fila.seleccionada = false;
            enviados++;
          }
          else fila.error = 'No se pudo enviar. Puedes reintentarlo para este pasajero.';
        });
        this.reconstruirFilas(true);
        this.hayFallos = enviados !== seleccionados.length;
        this.mensaje = `${enviados} de ${seleccionados.length} correos enviados.` + (this.hayFallos ? ' Los fallidos permanecen seleccionados para reintentar.' : '');
        this.actualizarReparto();
        this.envioRealizado = !this.hayFallos;
        return this.envioRealizado;
      } catch (error) {
        if (solicitud !== this.solicitud) return;
        const response = error && error.response;
        if (response && response.status === 422) {
          const errores = response.data && response.data.errors || {};
          Object.keys(errores).forEach(campo => {
            const match = campo.match(/^pasajeros\.(\d+)\./);
            if (match && seleccionados[Number(match[1])]) seleccionados[Number(match[1])].error = [].concat(errores[campo]).join(' ');
          });
          this.errorEnvio = 'Revisa los datos indicados.';
        } else {
          this.requiereActualizar = true;
          this.errorEnvio = 'No se ha podido confirmar el resultado del envío. Compruébalo antes de volver a enviar.';
        }
        return false;
      } finally {
        this.enviando = false;
        if (this.requiereActualizar && solicitud === this.solicitud) await this.cargar(true);
      }
    },
  },
};
</script>

<style scoped>
.correo-subtitulo { min-width: 190px; }
.correo-subtitulo ::v-deep input { font-size: 0.75rem; }
.correo-subtitulo:not(.v-input--is-focused) ::v-deep .v-input__slot::before { border-color: transparent !important; }
.campo-importe { min-width: 105px; }
.importe-resumen {
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.3;
  margin-top: 4px;
}
</style>
